import { useMemo, useState } from 'react';
import { Search, MapPin, Sun, Lightbulb, Building2, X } from 'lucide-react';
import { useCourts, type CourtFilters } from '../../hooks/useAPI';
import { CardSkeleton } from './Skeleton';
import FavoriteButton from './FavoriteButton';

interface CourtsScreenProps {
  onNavigate: (screen: any, params?: any) => void;
}

const SURFACES: Array<{ key: CourtFilters['surface']; label: string }> = [
  { key: undefined, label: 'All' },
  { key: 'hard',       label: 'Hard' },
  { key: 'clay',       label: 'Clay' },
  { key: 'grass',      label: 'Grass' },
  { key: 'carpet',     label: 'Carpet' },
  { key: 'artificial', label: 'Astro-turf' },
];

const SORTS = [
  { key: 'price-asc',  label: 'Price (low to high)' },
  { key: 'price-desc', label: 'Price (high to low)' },
  { key: 'name',       label: 'Name' },
] as const;

type SortKey = typeof SORTS[number]['key'];

export default function CourtsScreen({ onNavigate }: CourtsScreenProps) {
  const [surface, setSurface] = useState<CourtFilters['surface']>(undefined);
  const [indoor, setIndoor] = useState<boolean | undefined>(undefined);
  const [lights, setLights] = useState<boolean | undefined>(undefined);
  const [maxPrice, setMaxPrice] = useState<number>(400);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<SortKey>('price-asc');

  const { data: courts, isLoading } = useCourts({ surface, indoor, lights });

  const rows = useMemo(() => {
    const list = ((courts as any[]) || []).slice();

    const q = search.trim().toLowerCase();
    let filtered = list.filter((c) => {
      if (Number(c.hourly_rate_offpeak) > maxPrice) return false;
      if (!q) return true;
      return (
        c.name?.toLowerCase().includes(q)
        || c.clubs?.name?.toLowerCase().includes(q)
        || c.clubs?.address?.toLowerCase().includes(q)
        || (c.surface as string)?.toLowerCase().includes(q)
      );
    });

    filtered.sort((a, b) => {
      if (sort === 'name') {
        return (a.clubs?.name ?? '').localeCompare(b.clubs?.name ?? '')
          || (a.name ?? '').localeCompare(b.name ?? '');
      }
      const ap = Number(a.hourly_rate_offpeak);
      const bp = Number(b.hourly_rate_offpeak);
      return sort === 'price-asc' ? ap - bp : bp - ap;
    });

    return filtered;
  }, [courts, search, maxPrice, sort]);

  const anyFilter = surface || indoor !== undefined || lights !== undefined
    || search.trim() !== '' || maxPrice < 400;

  const clearAll = () => {
    setSurface(undefined); setIndoor(undefined); setLights(undefined);
    setSearch(''); setMaxPrice(400);
  };

  return (
    <div className="w-full min-h-screen bg-background pb-24">
      <div className="bg-gradient-to-br from-[#0F1E36] to-[#16294A] text-white px-6 pt-6 pb-6 rounded-b-[2rem]">
        <h1 className="text-2xl mb-1">Tennis courts</h1>
        <p className="text-sm opacity-80">Find and book courts across Dubai.</p>
      </div>

      <div className="px-6 -mt-4 mb-4">
        <div className="bg-card border border-border rounded-2xl p-3 flex items-center gap-2 shadow-sm">
          <Search className="w-4 h-4 text-muted-foreground" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search area, club, or court"
            className="flex-1 bg-transparent text-sm outline-none"
          />
          {search && (
            <button onClick={() => setSearch('')} className="text-muted-foreground hover:text-foreground">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      <div className="px-6 mb-3">
        <div className="flex gap-2 overflow-x-auto no-scrollbar">
          {SURFACES.map((s) => {
            const active = surface === s.key;
            return (
              <button
                key={s.label}
                onClick={() => setSurface(s.key)}
                className={`flex-shrink-0 h-8 px-3 rounded-full text-xs transition-all ${
                  active ? 'bg-[#1A6B4F] text-white' : 'bg-card border border-border'
                }`}
              >
                {s.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="px-6 mb-4 flex gap-2 flex-wrap">
        <FilterToggle icon={Building2} label="Indoor" active={indoor === true}
          onClick={() => setIndoor(indoor === true ? undefined : true)} />
        <FilterToggle icon={Sun} label="Outdoor" active={indoor === false}
          onClick={() => setIndoor(indoor === false ? undefined : false)} />
        <FilterToggle icon={Lightbulb} label="Lights" active={lights === true}
          onClick={() => setLights(lights === true ? undefined : true)} />
      </div>

      <div className="px-6 mb-4">
        <div className="bg-card border border-border rounded-xl p-3">
          <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
            <span>Max price</span>
            <span className="text-foreground font-medium">
              {maxPrice >= 400 ? 'Any' : `AED ${maxPrice} / hour`}
            </span>
          </div>
          <input
            type="range"
            min={50}
            max={400}
            step={10}
            value={maxPrice}
            onChange={(e) => setMaxPrice(Number(e.target.value))}
            className="w-full accent-[#1A6B4F]"
          />
        </div>
      </div>

      <div className="px-6 flex items-center justify-between mb-3">
        <div className="text-xs text-muted-foreground">
          {isLoading ? 'Loading…' : `${rows.length} court${rows.length === 1 ? '' : 's'}`}
        </div>
        <div className="flex items-center gap-2">
          <select value={sort} onChange={(e) => setSort(e.target.value as SortKey)}
            className="h-8 px-2 rounded-lg border border-border bg-card text-xs">
            {SORTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
          {anyFilter && (
            <button onClick={clearAll} className="text-xs text-[#1A6B4F] hover:underline">
              Clear
            </button>
          )}
        </div>
      </div>

      <div className="px-6 space-y-3">
        {isLoading ? (
          <>
            <CardSkeleton />
            <CardSkeleton />
            <CardSkeleton />
          </>
        ) : rows.length === 0 ? (
          <div className="text-center text-sm text-muted-foreground py-12">
            No courts match your filters.
          </div>
        ) : (
          rows.map((c: any) => (
            <button
              key={c.id}
              onClick={() => onNavigate('court', { id: c.id })}
              className="w-full text-left bg-card border border-border rounded-2xl overflow-hidden hover:border-[#1A6B4F]/40 transition-colors relative"
            >
              <div className="absolute top-3 right-3 z-10">
                <FavoriteButton target={{ kind: 'court', id: c.id }} variant="floating" />
              </div>
              <div className="aspect-[16/9] bg-secondary">
                {c.photos?.[0]?.url
                  ? <img src={c.photos[0].url} alt="" className="w-full h-full object-cover" />
                  : typeof c.photos?.[0] === 'string'
                    ? <img src={c.photos[0]} alt="" className="w-full h-full object-cover" />
                    : null}
              </div>
              <div className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-base font-medium truncate">
                      {c.clubs?.name ?? 'Unknown club'}
                    </div>
                    <div className="text-xs text-muted-foreground truncate capitalize">
                      {c.name} · {c.surface} · {c.is_indoor ? 'Indoor' : 'Outdoor'}
                      {c.has_lights ? ' · Lights' : ''}
                    </div>
                    {c.clubs?.address && (
                      <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        <span className="truncate">{c.clubs.address}</span>
                      </div>
                    )}
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-sm text-muted-foreground">from</div>
                    <div className="text-lg text-[#1A6B4F] font-medium">
                      {c.currency} {Number(c.hourly_rate_offpeak).toFixed(0)}
                    </div>
                    <div className="text-[11px] text-muted-foreground">/ hour</div>
                  </div>
                </div>
              </div>
            </button>
          ))
        )}
      </div>
    </div>
  );
}

function FilterToggle({
  icon: Icon, label, active, onClick,
}: { icon: any; label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={`h-8 px-3 rounded-full text-xs transition-all flex items-center gap-1.5 ${
        active ? 'bg-[#1A6B4F] text-white' : 'bg-card border border-border'
      }`}
    >
      <Icon className="w-3.5 h-3.5" />
      {label}
    </button>
  );
}
